import { useContext, useEffect, useRef } from "react";
import { QueryClientContext, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useWorkspaceId } from "../bridges/session";
import { mapMode } from "./mapAccess";

/** The new Map is what this address shows (the release line or the preview), with nothing in the address that changes its first box. */
export const opensPlainNewMap = (pathname: string, search: string, mode = mapMode()): boolean =>
  search === "" && (pathname.replace(/\/+$/, "") === "/next/map" || (pathname.replace(/\/+$/, "") === "/map" && mode === "next"));

/**
 * Mounted by the frame while it waits for the Google Maps script (map-perf 2026-11-03): on a plain /map it asks for the Map's
 * start and first pins meanwhile (api.ts, prewarmMap), once per page load. Renders nothing.
 */
export const MapPrewarm = () => (useContext(QueryClientContext) ? <MapPrewarmOn /> : null);

const MapPrewarmOn = () => {
  const { pathname, search } = useLocation();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  const asked = useRef(false);
  useEffect(() => {
    if (asked.current || ws === null || !opensPlainNewMap(pathname, search)) return;
    asked.current = true;
    // Loaded on demand: the Map's data code stays out of the frame's own bundle (the page needs it next anyway).
    void import("./api").then((m) => m.prewarmMap(client, ws)).catch(() => undefined);
  }, [pathname, search, ws, client]);
  return null;
};
